import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { useCurrency } from "../context/CurrencyContext";
import { useState } from "react";
import { Menu, X } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useI18n();
  const { currency, setCurrency } = useCurrency();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navLink = ({ isActive }) =>
    `text-[13px] transition-colors ${isActive ? "text-[#1d1d1f]" : "text-[#86868b] hover:text-[#1d1d1f]"}`;

  return (
    <header
      className="glass sticky top-0 z-50 border-b border-black/5"
      data-testid="site-header"
    >
      <div className="max-w-[1200px] mx-auto px-6 sm:px-8 h-12 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center"
          data-testid="brand-link"
          aria-label="Tafo"
        >
          {/* To switch to "Tafo.mg" version: replace src with "/logo-tafo-mg.svg" */}
          <img src="/logo-tafo.svg" alt="Tafo" className="h-7 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/vente" className={navLink} data-testid="nav-sale">
            {t.nav.sale}
          </NavLink>
          <NavLink to="/location-longue" className={navLink} data-testid="nav-rental-long">
            {t.nav.rentalLong}
          </NavLink>
          <NavLink to="/location-saisonniere" className={navLink} data-testid="nav-rental-short">
            {t.nav.rentalShort}
          </NavLink>
          <NavLink to="/conseil" className={navLink} data-testid="nav-consulting">
            {t.nav.consulting}
          </NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="bg-transparent text-[12px] font-medium uppercase tracking-wider text-[#86868b] hover:text-[#1d1d1f] transition-colors outline-none cursor-pointer"
            data-testid="currency-select"
            aria-label="Currency"
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="MGA">MGA</option>
          </select>
          <button
            onClick={toggle}
            className="text-[12px] font-medium uppercase tracking-wider text-[#86868b] hover:text-[#1d1d1f] transition-colors"
            data-testid="lang-toggle"
          >
            {lang === "fr" ? "EN" : "FR"}
          </button>

          {user ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/dashboard"
                  className="text-[13px] text-[#1d1d1f] hover:opacity-70"
                  data-testid="nav-dashboard"
                >
                  {t.nav.dashboard}
                </Link>
              )}
              <button
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="text-[13px] text-[#86868b] hover:text-[#1d1d1f]"
                data-testid="logout-btn"
              >
                {t.nav.logout}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="btn-pill btn-primary text-[13px] px-5 py-2"
              data-testid="header-login-btn"
            >
              {t.nav.login}
            </Link>
          )}
        </div>

        <button
          className="md:hidden text-[#1d1d1f]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 bg-white px-6 py-6 space-y-4" data-testid="mobile-menu">
          <NavLink to="/vente" onClick={() => setOpen(false)} className="block text-base text-[#1d1d1f]">
            {t.nav.sale}
          </NavLink>
          <NavLink to="/location-longue" onClick={() => setOpen(false)} className="block text-base text-[#1d1d1f]">
            {t.nav.rentalLong}
          </NavLink>
          <NavLink to="/location-saisonniere" onClick={() => setOpen(false)} className="block text-base text-[#1d1d1f]">
            {t.nav.rentalShort}
          </NavLink>
          <NavLink to="/conseil" onClick={() => setOpen(false)} className="block text-base text-[#1d1d1f]">
            {t.nav.consulting}
          </NavLink>
          <div className="pt-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={toggle} className="text-sm text-[#86868b]">
                {lang === "fr" ? "English" : "Français"}
              </button>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="bg-transparent text-sm text-[#86868b] outline-none"
                aria-label="Currency"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="MGA">MGA</option>
              </select>
            </div>
            {user ? (
              <button
                onClick={async () => {
                  await logout();
                  setOpen(false);
                  navigate("/");
                }}
                className="text-sm text-[#1d1d1f]"
              >
                {t.nav.logout}
              </button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="btn-pill btn-primary text-sm">
                {t.nav.login}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
